import { useState } from 'react';
import { useCities } from '@/hooks/useStoreSettings';

interface CitiesManagerProps {
  onToast: (msg: string) => void;
}

export default function CitiesManager({ onToast }: CitiesManagerProps) {
  const { cities, addCity, removeCity, resetCities } = useCities();
  const [newCity, setNewCity] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const handleAdd = () => {
    const trimmed = newCity.trim();
    if (!trimmed) { setError('يرجى إدخال اسم المدينة'); return; }
    if (cities.includes(trimmed)) { setError('هذه المدينة موجودة مسبقاً'); return; }
    setError('');
    addCity(trimmed);
    setNewCity('');
    onToast(`تم إضافة مدينة "${trimmed}"`);
  };

  const handleDelete = (city: string) => {
    removeCity(city);
    setDeleteConfirm(null);
    onToast(`تم حذف مدينة "${city}"`);
  };

  const handleReset = () => {
    resetCities();
    onToast('تم استعادة قائمة المدن الافتراضية');
  };

  const filtered = cities.filter(c => c.includes(search));

  return (
    <div className="flex flex-col gap-5">
      {/* Stats */}
      <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
        <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg">
          <i className="ri-map-pin-line text-[#1a1a1a] text-base"></i>
        </div>
        <div>
          <p className="text-xl font-black text-[#1a1a1a]">{cities.length}</p>
          <p className="text-xs text-gray-400">مدينة متاحة للتوصيل</p>
        </div>
        <button
          onClick={handleReset}
          className="mr-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1.5 cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-refresh-line text-sm"></i>
          استعادة الافتراضي
        </button>
      </div>

      {/* Add new city */}
      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
          <i className="ri-add-circle-line text-[#1a1a1a]"></i>
          إضافة مدينة جديدة
        </h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={newCity}
            onChange={e => { setNewCity(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="مثال: الدوادمي"
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          />
          <button
            onClick={handleAdd}
            className="bg-[#1a1a1a] text-white rounded-xl px-5 py-2.5 text-sm font-bold hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-add-line"></i>
            إضافة
          </button>
        </div>
        {error && (
          <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {error}
          </p>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="بحث في المدن..."
          className="w-full border border-gray-200 rounded-xl pr-9 pl-4 py-2.5 text-sm outline-none focus:border-gray-400 transition-colors"
        />
      </div>

      {/* Cities grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">لا توجد نتائج</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {filtered.map(city => (
            <div
              key={city}
              className="flex items-center justify-between gap-2 bg-white border border-gray-100 rounded-xl px-3 py-2.5 group hover:border-gray-200 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <i className="ri-map-pin-2-line text-gray-300 text-xs flex-shrink-0"></i>
                <span className="text-sm text-[#1a1a1a] font-medium truncate">{city}</span>
              </div>
              <button
                onClick={() => setDeleteConfirm(city)}
                className="w-6 h-6 flex items-center justify-center rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 text-gray-300 hover:text-red-500 transition-all cursor-pointer flex-shrink-0"
                title="حذف"
              >
                <i className="ri-close-line text-xs"></i>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-map-pin-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف المدينة</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              هل أنت متأكد من حذف مدينة <span className="font-black text-[#1a1a1a]">"{deleteConfirm}"</span>؟
              <br />
              <span className="text-xs text-red-400">لن يتمكن العملاء من اختيارها عند الطلب</span>
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
